import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import QuantityControl from "../helper/QuantityControl";
import useCart from "../hooks/useCart";
import useJWT from "../hooks/useJWT";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useAppConfig } from "../context/AppConfigContext";
import { formatCurrencyLocal } from "../utils/stringManipulator";

const CartSection = () => {
  const { config } = useAppConfig();
  const { user } = useJWT();
  const navigate = useNavigate();

  const {
    fetchCart,
    updateCart,
    removeCart,
    data,
    status,
    addStatus,
    removeStatus,
    error,
    resetStatus,
  } = useCart();

  const hasInvalidWholesaleQty = Array.isArray(data)
    ? data.some(
        (item) =>
          item?.isGrosir === true &&
          Number(item?.qty) < Number(item?.minQty ?? 1),
      )
    : false;

  useEffect(() => {
    if (user && user.id) {
      fetchCart(user.id);
    }
  }, [user]);

  useEffect(() => {
    if (addStatus === "done") {
      resetStatus();
      fetchCart(user.id);
    } else if (addStatus === "error") {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error || "Something went wrong while update qty.",
      });
      resetStatus();
    }
  }, [addStatus, user]);

  useEffect(() => {
    if (removeStatus === "done") {
      resetStatus();
      fetchCart(user.id);
    } else if (removeStatus === "error") {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error || "Something went wrong while remove item cart.",
      });
      resetStatus();
    }
  }, [removeStatus, user]);

  const onQtyUpdate = (qty, cartId) => {
    if (qty < 0) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Quantity cannot be less than zero.",
      });
      return;
    }
    const cartItem = {
      id: cartId,
      qty: qty,
    };
    updateCart(cartItem);
  };

  return (
    <section className="cart py-80">
      <div className="container container-md">
        <div className="row gy-4">
          <div className="col-xxl-9 col-lg-8">
            <div className="cart-table border border-gray-100 rounded-8 px-20 py-28">
              <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                <table className="table style-three">
                  <thead>
                    <tr>
                      <th className="h6 mb-0 text-lg fw-bold">Delete</th>
                      <th className="h6 mb-0 text-lg fw-bold">Product Name</th>
                      <th className="h6 mb-0 text-lg fw-bold">Price</th>
                      <th className="h6 mb-0 text-lg fw-bold">Quantity</th>
                      <th className="h6 mb-0 text-lg fw-bold">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(data) &&
                      data.length > 0 &&
                      data.map((item, index) => (
                        <tr>
                          <td>
                            <button
                              type="button"
                              className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                              onClick={() => {
                                removeCart(item.id);
                              }}
                            >
                              <i className="ph ph-trash text-2xl d-flex" />
                            </button>
                          </td>
                          <td>
                            <div className="table-product d-flex align-items-center gap-24">
                              <Link
                                to={`/product-details/${encodeURIComponent(
                                  item?.slug,
                                )}`}
                                className="table-product__thumb border border-gray-100 rounded-8 flex-center"
                              >
                                <img
                                  src={
                                    item.productPhotoData[0]?.url ||
                                    "/assets/images/no-image.png"
                                  }
                                  alt=""
                                />
                              </Link>
                              <div className="table-product__content text-start">
                                <h6
                                  className="title text-lg fw-semibold mb-8"
                                  style={{ maxWidth: "300px" }}
                                >
                                  <Link
                                    to={`/product-details/${encodeURIComponent(
                                      item?.slug,
                                    )}`}
                                    className="link text-line-2"
                                    tabIndex={0}
                                  >
                                    {item.productName || "Product Name"}
                                  </Link>
                                </h6>
                                <div className="flex-align gap-16 mb-16">
                                  {/* <div className="flex-align gap-6">
                                    <span className="text-md fw-medium text-warning-600 d-flex">
                                      <i className="ph-fill ph-star" />
                                    </span>
                                    <span className="text-md fw-semibold text-gray-900">
                                      4.8
                                    </span>
                                  </div>
                                  <span className="text-sm fw-medium text-gray-200">
                                    |
                                  </span> */}
                                  <span className="text-neutral-600 text-sm">
                                    {item.variantCombinationName || ""}
                                  </span>
                                </div>
                                <div className="flex-align gap-16 mb-16">
                                  {item?.isGrosir && (
                                    <span className="text-neutral-600 text-sm">
                                      {item?.isGrosir
                                        ? `(Wholesale, min order ${item?.minQty ?? 1})`
                                        : ""}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="text-lg h6 mb-0 fw-semibold">
                              {formatCurrencyLocal(item?.price, config?.currency)}
                            </span>
                          </td>
                          <td>
                            <QuantityControl
                              initialQuantity={item?.qty ?? 0}
                              minQuantity={
                                item?.isGrosir ? Number(item?.minQty ?? 1) : 1
                              }
                              submitQty={(qty) => onQtyUpdate(qty, item.id)}
                            />
                          </td>
                          <td>
                            <span className="text-lg h6 mb-0 fw-semibold">
                              {formatCurrencyLocal(
                                item?.price * item.qty,
                                config?.currency,
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {data && data.length === 0 && (
                <div className="cart-action d-flex flex-wrap justify-content-between align-items-center mt-40">
                  <img
                    src="/assets/images/payment/empty.svg"
                    width={"100%"}
                    alt=""
                  />
                </div>
              )}

              {/* <div className="flex-between flex-wrap gap-16 mt-16">
                <div className="d-block d-lg-flex flex-align gap-16">
                  <input
                    type="text"
                    className="common-input mb-10"
                    placeholder="Coupon Code"
                  />
                  <button
                    type="submit"
                    className="btn btn-main-one py-18 w-100 rounded-8 mb-10"
                  >
                    Apply Coupon
                  </button>
                </div>
              </div> */}
            </div>
          </div>
          <div className="col-xxl-3 col-lg-4">
            <div className="cart-sidebar border border-gray-100 rounded-8 px-20 py-20">
              <h6 className="text-xl mb-32">Cart Totals</h6>
              <div className="bg-color-three rounded-8 p-24">
                <div className="mb-32 flex-between gap-8">
                  <span className="text-gray-900 font-heading-two">
                    Subtotal
                  </span>
                  <span className="text-gray-900 fw-semibold">
                    {formatCurrencyLocal(
                      data && data.length > 0
                        ? data.reduce((acc, item) => acc + item.price * item.qty, 0)
                        : 0,
                      config?.currency,
                    )}
                  </span>
                </div>
                <div className="mb-0 flex-between gap-8">
                  <span className="text-gray-900 font-heading-two">
                    Extimated Delivery
                  </span>
                  <span className="text-gray-900 fw-semibold">
                    {formatCurrencyLocal(5, config?.currency)}
                  </span>
                </div>
                {/* <div className="mb-0 flex-between gap-8">
                  <span className="text-gray-900 font-heading-two">
                    Extimated Taxs
                  </span>
                  <span className="text-gray-900 fw-semibold">$ 0</span>
                </div> */}
              </div>
              <div className="bg-color-three rounded-8 p-24 mt-24">
                <div className="flex-between gap-8">
                  <span className="text-gray-900 text-xl fw-semibold">
                    Total
                  </span>
                  <span className="text-gray-900 text-xl fw-semibold">
                    {formatCurrencyLocal(
                      data && data.length > 0
                        ? data.reduce((acc, item) => acc + item.price * item.qty, 0) + 5
                        : 0,
                      config?.currency,
                    )}
                  </span>
                </div>
              </div>
              <button
                className="btn btn-main-one mt-20 py-18 w-100 rounded-8"
                onClick={() => {
                  navigate("/checkout");
                }}
                disabled={(data && data.length === 0) || hasInvalidWholesaleQty}
              >
                Proceed to checkout
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CartSection;
